// Pill-shaped cluster marker rendered when milestones collide on the active
// window. Border colour reflects the most-severe lifecycle/risk state in
// the cluster; mini-bullets summarise the status mix at a glance.
//
// Severity scope: cluster aggregation reads the LIFECYCLE base for non-late
// milestones. The `due-soon` calendar overlay is intentionally collapsed
// into the `upcoming` bucket here — the cluster preview is a triage glance
// where Late and Delivered late are the actionable signals; surfacing
// every due-soon milestone as its own brand-violet bullet would dilute
// that signal. Users still see the explicit Due soon pill once they open
// the cluster popover or scroll to the row in the table. The timeline
// status legend keeps Due soon as a distinct swatch to communicate the
// vocabulary; the divergence is documented in
// .claude/skills/contractgen-brand/SKILL.md (Cluster Bubble section).

import { useState } from "react";
import { clsx } from "clsx";
import type { Milestone } from "../../../../types";
import { deriveMilestoneDisplayStatus } from "../../../../utils/deriveMilestoneDisplayStatus";
import { deriveTimelineDotLifecycle } from "../../../../utils/deriveMilestoneDisplayLifecycle";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import { effectiveTimelineDate } from "./timelineGeometry";

const MAX_VISIBLE_BULLETS = 5;

type SeverityKey =
  | "late"
  | "delivered-late"
  | "partially-accepted"
  | "partial-paid"
  | "delivered"
  | "upcoming"
  | "accepted"
  | "paid";

const SEVERITY_ORDER: ReadonlyArray<SeverityKey> = [
  "late",
  "delivered-late",
  "partially-accepted",
  // Partial+paid sits in the same triage tier as partially-accepted —
  // payment landed but full acceptance still hasn't, so the actionable
  // signal is the partial-accept side, not the paid side.
  "partial-paid",
  "delivered",
  "upcoming",
  "accepted",
  "paid",
];

const SEVERITY_DOT_CLASS: Record<SeverityKey, string> = {
  late: "bg-red-500",
  "delivered-late": "bg-amber-500",
  "partially-accepted": "bg-sky-500",
  // Hex literals match timelineDotClass.ts so the cluster bullet and the
  // timeline dot use the same gradient token. Emerald-500 = #10b981,
  // Amber-500 = #f59e0b.
  "partial-paid": "bg-[linear-gradient(90deg,#10b981_50%,#f59e0b_50%)]",
  delivered: "bg-sky-500",
  upcoming: "bg-slate-400",
  accepted: "bg-emerald-500",
  paid: "bg-emerald-600",
};

const SEVERITY_BORDER_CLASS: Record<SeverityKey, string> = {
  late: "border-red-500",
  "delivered-late": "border-amber-500",
  "partially-accepted": "border-sky-500",
  // Amber border conveys the still-needs-attention edge of the partial-
  // accept side; the bullet itself carries the split-gradient signal.
  "partial-paid": "border-amber-500",
  delivered: "border-sky-500",
  upcoming: "border-slate-400",
  accepted: "border-emerald-500",
  paid: "border-emerald-600",
};

const SEVERITY_LABEL: Record<SeverityKey, string> = {
  late: "late",
  "delivered-late": "delivered late",
  "partially-accepted": "partially accepted",
  "partial-paid": "partially accepted (paid)",
  delivered: "delivered",
  upcoming: "upcoming",
  accepted: "accepted",
  paid: "paid",
};

function severityFor(milestone: Milestone, today?: string): SeverityKey {
  const display = deriveMilestoneDisplayStatus(milestone, today);
  if (display.riskOverlay === "late") return "late";
  if (display.riskOverlay === "delivered-late") return "delivered-late";
  // due-soon overlay collapses into `upcoming` per the file-top severity-scope note.
  // For lifecycle, use the timeline-view derivation so partial+paid yields the
  // dedicated `partial-paid` key (split-gradient bullet) instead of collapsing
  // to the plain `paid` lifecycle the underlying status uses.
  const dotLifecycle = deriveTimelineDotLifecycle(milestone);
  if (dotLifecycle === "partial-paid") return "partial-paid";
  if (dotLifecycle === "partially-accepted") return "partially-accepted";
  if (dotLifecycle === "delivered") return "delivered";
  if (dotLifecycle === "upcoming") return "upcoming";
  if (dotLifecycle === "accepted") return "accepted";
  return "paid";
}

interface SeverityCounts {
  total: number;
  byKey: Map<SeverityKey, number>;
}

function tally(milestones: readonly Milestone[], today?: string): SeverityCounts {
  const byKey = new Map<SeverityKey, number>();
  for (const m of milestones) {
    const key = severityFor(m, today);
    byKey.set(key, (byKey.get(key) ?? 0) + 1);
  }
  return { total: milestones.length, byKey };
}

function mostSevere(counts: SeverityCounts): SeverityKey {
  for (const key of SEVERITY_ORDER) {
    if ((counts.byKey.get(key) ?? 0) > 0) return key;
  }
  return "paid";
}

function buildSeverityLabel(counts: SeverityCounts): string {
  const parts: string[] = [];
  for (const key of SEVERITY_ORDER) {
    const n = counts.byKey.get(key) ?? 0;
    if (n > 0) parts.push(`${n} ${SEVERITY_LABEL[key]}`);
  }
  return parts.join(", ");
}

export interface ClusterBubbleProps {
  milestones: Milestone[];
  centreIso: string;
  today?: string;
  open?: boolean;
  onActivate: () => void;
  /** Callback ref for the inner button. Allows the parent (TimelineActiveWindow)
   *  to track per-cluster bubble nodes so focus can return to the triggering
   *  bubble when its popover dismisses (WCAG 2.4.3). */
  buttonRef?: (node: HTMLButtonElement | null) => void;
}

function clusterDateRange(milestones: readonly Milestone[]): string {
  // Mirrors the timeline's plotting rule: an actual lifecycle date wins
  // over the planned date so the tooltip range matches where the bubble
  // actually sits.
  const dates = milestones
    .map(effectiveTimelineDate)
    .filter((d): d is string => typeof d === "string")
    .sort();
  if (dates.length === 0) return "";
  if (dates[0] === dates[dates.length - 1]) return formatDayMonthYear(dates[0]);
  return `${formatDayMonthYear(dates[0])} – ${formatDayMonthYear(dates[dates.length - 1])}`;
}

export default function ClusterBubble({
  milestones,
  centreIso,
  today,
  open = false,
  onActivate,
  buttonRef,
}: ClusterBubbleProps) {
  const [hovered, setHovered] = useState(false);
  const counts = tally(milestones, today);
  const severity = mostSevere(counts);
  // Border colour is severity-driven only when every item in the cluster
  // shares the same severity. Mixed clusters (e.g. one delivered-late + one
  // upcoming) read with a neutral slate border so the per-item bullets
  // carry the severity signal — a single red bullet is enough to communicate
  // "one item needs attention" without painting the whole bubble red.
  const isUniform =
    Array.from(counts.byKey.values()).filter((n) => n > 0).length === 1;
  const borderClass = isUniform
    ? SEVERITY_BORDER_CLASS[severity]
    : "border-slate-300 dark:border-slate-600";
  const label = `Cluster of ${counts.total} milestones, ${buildSeverityLabel(counts)}. Click to expand.`;

  // Build the visible bullet sequence in severity order, expanded by count.
  const allBullets: SeverityKey[] = [];
  for (const key of SEVERITY_ORDER) {
    const n = counts.byKey.get(key) ?? 0;
    for (let i = 0; i < n; i += 1) allBullets.push(key);
  }
  const visible = allBullets.slice(0, MAX_VISIBLE_BULLETS);
  const overflow = Math.max(0, allBullets.length - MAX_VISIBLE_BULLETS);

  const tooltipDateRange = clusterDateRange(milestones);

  return (
    <button
      type="button"
      ref={buttonRef}
      data-testid={`cluster-bubble-${centreIso}`}
      data-severity={severity}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={label}
      onClick={onActivate}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      className={clsx(
        "absolute left-0 top-0 -translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-1 rounded-full border-2 bg-white px-2 py-1 text-caption font-semibold focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:bg-slate-800",
        "min-h-[40px]",
        borderClass,
      )}
    >
      <span className="text-slate-700 dark:text-slate-100">×{counts.total}</span>
      <span className="inline-flex items-center gap-0.5" aria-hidden="true">
        {visible.map((key, i) => (
          <span
            key={`${key}-${i}`}
            className={clsx("h-1.5 w-1.5 rounded-full", SEVERITY_DOT_CLASS[key])}
          />
        ))}
        {overflow > 0 && (
          <span className="ml-0.5 text-caption text-slate-500 dark:text-slate-400">
            +{overflow}
          </span>
        )}
      </span>
      {hovered && !open && (
        <div
          data-testid="cluster-bubble-tooltip"
          role="tooltip"
          className="pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 w-48 rounded-md border border-slate-200 bg-white p-3 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800"
        >
          <p className="text-caption font-semibold text-slate-700 dark:text-slate-200">
            {counts.total} milestones
          </p>
          {tooltipDateRange && (
            <p className="mt-0.5 text-caption text-slate-500 dark:text-slate-400">
              {tooltipDateRange}
            </p>
          )}
        </div>
      )}
    </button>
  );
}
